/**
 * Timezone conversion utilities
 * Provides dayjs equivalents for date-fns-tz functions
 */
import dayjs, { type Dayjs } from './dayjs-config';
import { toDayjs } from './parse-iso-safe';

/**
 * Convert a date to a specific timezone
 * Equivalent to date-fns-tz's toZonedTime
 *
 * @param date - Date to convert
 * @param timezone - Target timezone (e.g., 'America/New_York')
 * @returns dayjs instance in the target timezone
 *
 * @example
 * const utcDate = dayjs.utc('2024-01-15T10:00:00Z');
 * const nyTime = toZonedTime(utcDate, 'America/New_York');
 * // Same moment in time, but displayed in NY timezone
 */
export function toZonedTime(date: Date | Dayjs | string | null | undefined, timezone: string): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  try {
    return dayjsDate.tz(timezone);
  } catch {
    // Invalid timezone, return UTC
    return dayjsDate.utc();
  }
}

/**
 * Convert a date from a specific timezone to UTC
 * Equivalent to date-fns-tz's fromZonedTime
 *
 * @param date - Date in the source timezone
 * @param timezone - Source timezone
 * @returns dayjs instance in UTC
 *
 * @example
 * // User enters "10:00 AM" in New York time
 * const nyTime = dayjs.tz('2024-01-15 10:00', 'America/New_York');
 * const utcTime = fromZonedTime(nyTime, 'America/New_York');
 * // Converts to UTC for storage
 */
export function fromZonedTime(date: Date | Dayjs | string | null | undefined, timezone: string): Dayjs | null {
  const dayjsDate = toDayjs(date);
  if (!dayjsDate) return null;

  try {
    // If the date doesn't have timezone info, treat it as being in the source timezone
    const zonedDate = dayjsDate.tz(timezone, true);
    return zonedDate.utc();
  } catch {
    return dayjsDate.utc();
  }
}

/**
 * Format a date in a specific timezone
 * Equivalent to date-fns-tz's formatInTimeZone
 *
 * @param date - Date to format
 * @param timezone - Target timezone
 * @param formatString - Format string (dayjs format tokens)
 * @returns Formatted date string
 *
 * @example
 * formatInTimezone(new Date(), 'America/New_York', 'YYYY-MM-DD HH:mm:ss')
 * // "2024-01-15 10:30:00"
 */
export function formatInTimezone(
  date: Date | Dayjs | string | null | undefined,
  timezone: string,
  formatString: string,
): string {
  const zonedDate = toZonedTime(date, timezone);
  if (!zonedDate) return '';

  return zonedDate.format(formatString);
}

/**
 * Get the timezone offset string for a given timezone
 *
 * @param timezone - Timezone name
 * @param date - Optional date for DST calculation (defaults to now)
 * @returns Offset string like "+05:00" or "-08:00"
 */
export function getTimezoneOffset(timezone: string, date?: Date | Dayjs): string {
  try {
    const dayjsDate = date ? toDayjs(date) : dayjs();
    if (!dayjsDate) return '+00:00';

    return dayjsDate.tz(timezone).format('Z');
  } catch {
    return '+00:00';
  }
}

/**
 * Get the timezone abbreviation for a given timezone
 *
 * @param timezone - Timezone name
 * @param date - Optional date for DST calculation (defaults to now)
 * @returns Abbreviation like "EST", "PDT", etc.
 */
export function getTimezoneAbbreviation(timezone: string, date?: Date | Dayjs): string {
  try {
    const dayjsDate = date ? toDayjs(date) : dayjs();
    if (!dayjsDate) return 'UTC';

    return dayjsDate.tz(timezone).format('z');
  } catch {
    return 'UTC';
  }
}
